import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Send, Share2, ArrowRight } from 'lucide-react';

type HeroFRProps = {
  variant?: 'agents' | 'partenaires';
};

const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const stagger = { animate: { transition: { staggerChildren: 0.1 } } };

const HeroFR: React.FC<HeroFRProps> = ({ variant = 'agents' }) => {
  const isPartners = variant === 'partenaires';
  const signupPath = isPartners ? '/fr/partenaires/signup' : '/fr/signup';

  return (
    <>
      <section className="pt-12 sm:pt-16 md:pt-24 pb-12 sm:pb-16">
        <motion.div
          className="container mx-auto px-4 sm:px-6 lg:px-8"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col justify-center text-center">
              <motion.h1
                className="font-manrope text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {isPartners ? (
                  <>
                    <span className="text-[#3C59B2]">La vidéo qui renforce</span>
                    <br />
                    votre offre Matterport
                  </>
                ) : (
                  <>
                    Envoyez votre lien Matterport, <span className="text-[#3C59B2]">recevez une vidéo</span>
                  </>
                )}
              </motion.h1>
              <motion.p
                className="font-manrope mt-4 sm:mt-6 text-sm sm:text-base md:text-lg text-gray-600 font-medium leading-relaxed px-2 sm:px-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {isPartners ? (
                  'Envoyez votre lien Matterport, recevez une vidéo le lendemain'
                ) : (
                  'Rien à filmer, rien à monter, livraison le lendemain'
                )}
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 sm:mt-10 md:mt-12 justify-center items-center px-4 sm:px-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {isPartners ? (
                  <>
                    <a
                      href={`${signupPath}?section=try-one`}
                      className="btn-premium w-full sm:w-[200px] md:w-[220px] lg:w-[240px] h-[48px] sm:h-[52px] md:h-[56px] bg-white text-[#3C59B2] border-2 border-[#3C59B2] rounded-full overflow-hidden font-bold text-sm sm:text-base md:text-lg hover:bg-slate-50 transition-all flex items-center justify-center"
                    >
                      Tester sur une propriété
                    </a>
                    <a
                      href={signupPath}
                      className="btn-premium w-full sm:w-[220px] md:w-[240px] h-[48px] sm:h-[52px] md:h-[56px] bg-[#3C59B2] text-white rounded-full overflow-hidden font-bold text-sm sm:text-base md:text-lg hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2"
                    >
                      Devenir partenaire <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  </>
                ) : (
                  <>
                    <a
                      href={`${signupPath}?section=try-one`}
                      className="btn-premium w-full sm:w-[200px] md:w-[220px] lg:w-[240px] h-[48px] sm:h-[52px] md:h-[56px] bg-white text-[#3C59B2] border-2 border-[#3C59B2] rounded-full overflow-hidden font-bold text-sm sm:text-base md:text-lg hover:bg-slate-50 transition-all flex items-center justify-center"
                    >
                      Tester sur une propriété
                    </a>
                    <a
                      href={signupPath}
                      className="btn-premium w-full sm:w-[220px] md:w-[240px] lg:w-[260px] h-[48px] sm:h-[52px] md:h-[56px] bg-[#3C59B2] text-white rounded-full overflow-hidden font-bold text-sm sm:text-base md:text-lg hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2"
                    >
                      Commander ma vidéo <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 lg:gap-16">
              {[
                { title: '24h chrono', text: 'Vidéo livrée le lendemain (du lundi au jeudi)', icon: Clock },
                { title: 'Zéro effort', text: 'Vous envoyez le lien Matterport, on fait le reste', icon: Send },
                { title: 'Prêt à diffuser', text: isPartners ? 'Sur les sites, portails et réseaux sociaux de vos clients' : 'Sur votre site, portails et réseaux sociaux', icon: Share2, spanClass: 'sm:col-span-2 md:col-span-1 sm:justify-self-center' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className={`card-hover bg-white border border-[#E2E8F0] rounded-3xl flex flex-col items-center text-center p-6 ${item.spanClass || ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-3 sm:mb-4 bg-transparent border-2 border-[#3C59B2]">
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#3C59B2]" />
                  </div>
                  <h3 className="font-manrope text-lg sm:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroFR;
